import { formatTime, formatDate } from '@smarttransit/common'
import { isAuthorized, addAlert } from '../../../../../utilities/helpers'

import {
  blacklistPhone,
  getPhoneBlacklistById,
  findPhoneBlacklist,
  restorePhone
} from '../../../../../services/phone-blacklist-service'

export default {
  props: {
    signedInUser: Object,
    phoneBlacklistId: String
  },
  data () {
    return {
      name: 'passengers-phone-blacklist-phone',
      apiInProgress: false,
      restoreInProgress: false,
      isAdmin: false,
      threatLevelItems: [
        { text: 'Suspicious (1)', value: 1 },
        { text: 'High Risk (2)', value: 2 }
      ],
      record: null,
      history: [],
      historyLoading: false,
      historyHeaders: [
        { text: 'Date created', value: 'dateCreated' },
        { text: 'Status', value: 'status' },
        { text: 'Blacklisted by', value: 'byStUserId' },
        { text: 'Restored by', value: 'restoredByStUserId' }
      ],
      createForm: {
        phoneNumber: '',
        phoneNumberConfirm: '',
        threatLevel: 1,
        note: ''
      },
      editNotes: [],
      newNoteText: ''
    }
  },
  computed: {
    isNew () {
      return !this.phoneBlacklistId || String(this.phoneBlacklistId) === '0'
    },
    isEditable () {
      return this.isAdmin && !this.apiInProgress && !this.restoreInProgress
    },
    currentPhoneNumber () {
      if (this.isNew) {
        return String(this.createForm.phoneNumber || '').trim()
      }
      return this.record ? this.record.phoneNumber : ''
    },
    last4Code () {
      return getLast4Digits(this.currentPhoneNumber)
    },
    canCreate () {
      const a = String(this.createForm.phoneNumber || '').trim()
      const b = String(this.createForm.phoneNumberConfirm || '').trim()
      return Boolean(a && b && a === b)
    },
    canRestore () {
      return !this.isNew && this.record && !this.record.dateRestored
    },
    defaultPrefixForPhone () {
      // If confirm has a prefix selected but phoneNumber doesn't yet, seed phoneNumber with confirm's prefix.
      const phonePrefix = this.extractPrefixFromValue(this.createForm.phoneNumber)
      const confirmPrefix = this.extractPrefixFromValue(this.createForm.phoneNumberConfirm)
      if (!phonePrefix && confirmPrefix) return confirmPrefix
      return undefined
    },
    defaultPrefixForConfirm () {
      // If phoneNumber has a prefix selected but confirm doesn't yet, seed confirm with phoneNumber's prefix.
      const phonePrefix = this.extractPrefixFromValue(this.createForm.phoneNumber)
      const confirmPrefix = this.extractPrefixFromValue(this.createForm.phoneNumberConfirm)
      if (!confirmPrefix && phonePrefix) return phonePrefix
      return undefined
    },
    computedTitle () {
      if (this.isNew) return 'Blacklist Phone'
      if (this.record && this.record.dateRestored) return 'Restored Blacklist Record'
      return 'Edit Blacklisted Phone'
    }
  },
  async mounted () {
    // this.breadcrumbs = initBreadcrumbs.call(this)
    this.isAdmin = isAuthorized(this.$props.signedInUser, 'admin')
    await this.load()
  },
  methods: {
    async load () {
      if (this.isNew) {
        this.record = null
        this.history = []
        this.editNotes = []
        return
      }

      this.apiInProgress = true
      try {
        const rec = await getPhoneBlacklistById(this.phoneBlacklistId)

        this.record = {
          ...rec,
          dateCreatedLabel: `${formatTime(rec.dateCreated)}, ${formatDate(rec.dateCreated)}`,
          dateUpdatedLabel: `${formatTime(rec.dateUpdated)}, ${formatDate(rec.dateUpdated)}`,
          dateRestoredLabel: rec.dateRestored ? `${formatTime(rec.dateRestored)}, ${formatDate(rec.dateRestored)}` : null
        }

        this.editNotes = normalizeNotes(rec && rec.notes)
        await this.loadHistory()
      } catch (err) {
        addAlert({ message: err, type: 'error' })
      } finally {
        this.apiInProgress = false
      }
    },
    async loadHistory () {
      if (!this.record || !this.record.phoneNumber) {
        this.history = []
        return
      }

      this.historyLoading = true

      try {
        const items = await findPhoneBlacklist({
          where: { phoneNumber: this.record.phoneNumber, id: { neq: this.record.id } },
          order: 'dateCreated DESC',
          limit: 100
        })
        this.history = Array.isArray(items) ? items : []

        this.history = this.history.map(item => ({
          ...item,
          dateCreatedLabel: `${formatTime(item.dateCreated)}, ${formatDate(item.dateCreated)}`,
          dateUpdatedLabel: `${formatTime(item.dateUpdated)}, ${formatDate(item.dateUpdated)}`,
          dateRestoredLabel: item.dateRestored ? `${formatTime(item.dateRestored)}, ${formatDate(item.dateRestored)}` : null
        }))
      } catch (err) {
        addAlert({ message: err, type: 'error' })
      } finally {
        this.historyLoading = false
      }
    },
    cancel () {
      this.$router.push({ name: 'passengers-phone-blacklist' })
    },
    async create () {
      if (!this.isAdmin) return

      if (!this.canCreate) {
        return alert('Phone numbers do not match. Please enter the phone number twice to confirm.')
      }

      if (!this.createForm.note) {
        return alert('Please enter a note to blacklist this phone number.')
      }

      const code = this.last4Code

      if (!code) {
        return alert('Unable to compute last 4 digits for confirmation. Please ensure a valid phone number is entered.')
      }

      const promptResult = prompt(`Enter ${code} below to confirm blacklisting this phone number`)
      if (promptResult === null) return

      if (String(promptResult).trim() !== code) {
        return alert('Incorrect code, please try again')
      }

      this.apiInProgress = true

      try {
        const phoneNumber = String(this.createForm.phoneNumber || '').trim()
        const note = this.createForm.note || undefined
        const threatLevel = this.createForm.threatLevel
        await blacklistPhone({ phoneNumber, threatLevel, note })
        addAlert({ message: `Successfully blacklisted ${phoneNumber}`, type: 'success' })
        this.$router.replace({ name: 'passengers-phone-blacklist' })
      } catch (err) {
        addAlert({ message: err, type: 'error' })
      } finally {
        this.apiInProgress = false
      }
    },
    addNote () {
      if (!this.newNoteText || !String(this.newNoteText).trim()) return
      this.editNotes = normalizeNotes(this.editNotes)

      this.editNotes.unshift({
        new: true,
        date: new Date().toISOString(),
        stUserId: this.$props.signedInUser ? this.$props.signedInUser.id : null,
        note: String(this.newNoteText)
      })

      this.newNoteText = ''
    },
    deleteNote (idx) {
      if (confirm('Are you sure you want to delete this note: "' + this.editNotes[idx].note.substring(0, 20) + '..."')) {
        this.editNotes.splice(idx, 1)
      }
    },
    async saveNotes () {
      if (!this.isAdmin || !this.record) return
      this.apiInProgress = true

      try {
        await blacklistPhone({
          phoneNumber: this.record.phoneNumber,
          threatLevel: this.record.threatLevel,
          note: normalizeNotes(this.editNotes).map(note => { delete note.new; return note })
        })

        addAlert({ message: 'Saved notes', type: 'success' })
        await this.load()
      } catch (err) {
        addAlert({ message: err, type: 'error' })
      } finally {
        this.apiInProgress = false
      }
    },
    async restore () {
      if (!this.isAdmin || !this.canRestore) return
      const code = this.last4Code
      const promptResult = prompt(`Enter ${code} below to confirm RESTORING this phone number`)
      if (promptResult === null) return

      if (String(promptResult).trim() !== code) {
        return alert('Incorrect code, please try again')
      }

      this.restoreInProgress = true

      try {
        const phoneNumber = this.record.phoneNumber
        await restorePhone({ phoneNumber })
        addAlert({ message: `Successfully restored ${phoneNumber}`, type: 'success' })
        await this.load()
      } catch (err) {
        addAlert({ message: err, type: 'error' })
      } finally {
        this.restoreInProgress = false
      }
    },
    extractPrefixFromValue (value) {
      value = String(value || '').trim()
      if (!value || value.indexOf('+') !== 0) return null

      const items = this.$store && this.$store.getters && this.$store.getters.phoneNumberCountryCodes
        ? this.$store.getters.phoneNumberCountryCodes
        : []

      if (!Array.isArray(items) || !items.length) return null

      // Choose the longest matching prefix (defensive if codes ever overlap)
      let best = null
      items.forEach((item) => {
        if (item && item.code && value.indexOf(item.code) === 0) {
          if (!best || String(item.code).length > String(best).length) {
            best = String(item.code)
          }
        }
      })

      return best
    }
  }
}

function normalizeNotes (notes) {
  if (!notes) return []

  let notesArray = []

  if (Array.isArray(notes)) {
    notesArray = notes
  } else {
    notesArray = [notes]
  }

  return notesArray.map(note => ({
    ...note,
    dateLabel: `${formatTime(note.date)}, ${formatDate(note.date)}`
  }))
}

function getLast4Digits (phoneNumber) {
  const digits = String(phoneNumber || '').replace(/[^\d]+/g, '')
  if (!digits || digits.length < 4) return ''
  return digits.substring(digits.length - 4)
}
